import React, { useEffect, useState, useCallback } from "react";
import TaskInformation from "../TaskInformation";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import ValidationButton from "../ValidationButton";
import PreviewButton from "../PreviewButton";
import { useDispatch, useSelector } from "react-redux";
import { completeTask, getTask } from "../../../store/action/taskAction";
import { getAllFileForClaim } from "../../../store/action/fileManagement";
import ImagesTab from "../../sinistreSection/photoSection";
import { useNavigate, useParams } from "react-router-dom";
import ErrorsAlert from "../../../utils/errorsAlert";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
  },
  paper: {
    borderRadius: 11,
    width: "100%",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.06)",
  },
  mainContent: {
    width: "95%",
    display: "flex",
    flexDirection: "column",
  },
}));

const UploadPictureBeforeRep = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();

  const username = localStorage.getItem("username");
  const [isTaskClaimed, setIsTaskClaimed] = useState(true);
  const [avantRepPicturesUploaded, setAvantRepPicturesUploaded] = useState(false);

  const [task, setTask] = useState(null);
  const [variables, setVariables] = useState({});
  const [infoSinistre, setInfoSinistre] = useState("");
  const [claimNumber, setClaimNumber] = useState("");

  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [errors, setErrors] = useState([]);

  // Get task from Redux store (if available)
  const taskFromStore = useSelector((state) =>
    state.taskReducer?.taskList?.find((t) => t?.taskId === taskId)
  );

  // Load initial task from store (if any)
  useEffect(() => {
    if (taskFromStore) {
      setTask(taskFromStore);
      setVariables(taskFromStore.variables || {});
      setInfoSinistre(taskFromStore.businessKey || "");
      setClaimNumber(taskFromStore.businessKey || "");
    }
  }, [taskFromStore]);

  // Fetch latest task from backend on mount
  useEffect(() => {
    const fetchTask = async () => {
      try {
        const response = await getTask(taskId, username);
        setTask(response);
        setVariables(response?.variables || {});
        setInfoSinistre(response?.businessKey || "");
        setClaimNumber(response?.businessKey || "");
      } catch (error) {
        console.error("Error fetching task:", error);
        setTask(null);
        setClaimNumber("");
        setInfoSinistre("");
      }
    };
    fetchTask();
  }, [dispatch, taskId, username]);

  // Keep claim status in sync
  useEffect(() => {
    setIsTaskClaimed(task?.assignee !== username);
  }, [task, username]);

  // Track images uploaded from ImagesTab
  const handleAvantRepPicturesChange = useCallback((images) => {
    if (Array.isArray(images) && images.length > 0) {
      setAvantRepPicturesUploaded(true);
    } else if (typeof images === "number" && images > 0) {
      setAvantRepPicturesUploaded(true);
    } else {
      setAvantRepPicturesUploaded(false);
    }
  }, []);

  // Check server for existing "Avant réparation" pictures
  const refreshAvantRepPicturesState = useCallback(async () => {
    if (!claimNumber) return false;
    try {
      const files = await dispatch(getAllFileForClaim(claimNumber));
      const hasBefore = Array.isArray(files)
        ? files.some((file) =>
            Array.isArray(file?.metadata) &&
            file.metadata.some((m) => m?.value === "PICTURE_BEFORE_REPARATION")
          )
        : false;
      setAvantRepPicturesUploaded(Boolean(hasBefore));
      return Boolean(hasBefore);
    } catch (e) {
      // keep previous state if fetch fails
      return avantRepPicturesUploaded;
    }
  }, [dispatch, claimNumber, avantRepPicturesUploaded]);

  // When claim number is available, check initially
  useEffect(() => {
    if (claimNumber) {
      refreshAvantRepPicturesState();
    }
  }, [claimNumber, refreshAvantRepPicturesState]);

 

  // Submit/complete task
  const ValidationAction = async () => {
    // Ensure we have the latest files state before validating and use the fresh value
    const hasBeforeNow = await refreshAvantRepPicturesState();
    const validationErrors = [];
    if (!hasBeforeNow) {
      validationErrors.push("Merci d'ajouter les photos AVANT réparation.");
    }
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setOpenErrorDialog(true);
      return;
    }

    try {
        // console.log("done");
      await dispatch(completeTask(taskId, username, variables));
      navigate("/todo-list", { reload: true });
    } catch (error) {
      console.error("Task completion error:", error);
      setErrors(["Erreur lors de la validation. Veuillez réessayer."]);
      setOpenErrorDialog(true);
    }
  };

  const handleClose = () => {
    setOpenErrorDialog(false);
    setErrors([]);
  };

  return (
    <div className={classes.root}>
      <div style={{ display: "flex" }}>
        <div className={classes.mainContent}>
          <TaskInformation taskId={taskId} onClaimStatusChange={setIsTaskClaimed} />

          {claimNumber ? (
            <Paper className={classes.paper}>
              <ImagesTab
                claimNumber={claimNumber}
                isTaskClaimed={isTaskClaimed}
                onAvantRepPicturesChange={handleAvantRepPicturesChange}
              />
            </Paper>
          ) : (
            <div
              style={{
                padding: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
                flexDirection: "column",
                color: "#818181",
                background: "#F7F8FA",
                borderRadius: 11,
                border: "1px solid #ECECEC",
              }}
            >
              <svg
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginBottom: 16 }}
              >
                <circle cx="20" cy="20" r="20" fill="#E9EAEF" />
                <path
                  d="M25 23V17H15V23"
                  stroke="#A0A4B8"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 20C18 20.5523 17.5523 21 17 21C16.4477 21 16 20.5523 16 20C16 19.4477 16.4477 19 17 19C17.5523 19 18 19.4477 18 20Z"
                  fill="#A0A4B8"
                />
                <rect
                  x="13"
                  y="14"
                  width="14"
                  height="12"
                  rx="2"
                  stroke="#A0A4B8"
                  strokeWidth="1.5"
                />
              </svg>
              <div style={{ fontSize: 16, fontWeight: 500 }}>Chargement...</div>
            </div>
          )}
        </div>

        <PreviewButton claimData={infoSinistre} />
      </div>

      <ValidationButton
        handleValidation={() => (isTaskClaimed ? null : ValidationAction())}
      />

      <ErrorsAlert open={openErrorDialog} errors={errors} handleClose={handleClose} />
    </div>
  );
};

export default UploadPictureBeforeRep;
